import { ApiError, apiFetch } from '@/lib/api';
import type { AnimalDeletionRelatedItem, AnimalDeletionSummary, AnimalSummary } from '@/api/hooks';
import type { AnimalTimelineResponse } from '@/types/api';

export type AnimalDeletionSummaryResult = AnimalDeletionSummary & {
  uncertain?: boolean;
};

function buildSummary(
  animal: AnimalSummary,
  counts: { media: number; events: number; movements: number; offspring: number },
  uncertain = false,
): AnimalDeletionSummaryResult {
  const related: AnimalDeletionRelatedItem[] = [];

  if (counts.media > 0) {
    related.push({ key: 'media', label: 'Fotos e vídeos', count: counts.media });
  }
  if (counts.events > 0) {
    related.push({ key: 'events', label: 'Eventos de manejo', count: counts.events });
  }
  if (counts.movements > 0) {
    related.push({ key: 'movements', label: 'Movimentações', count: counts.movements });
  }
  if (counts.offspring > 0) {
    related.push({
      key: 'offspring',
      label: 'Vínculos genealógicos com filhos',
      count: counts.offspring,
    });
  }

  const total = counts.media + counts.events + counts.movements + counts.offspring;

  return {
    animal: {
      id: animal.id,
      internal_number: animal.internal_number,
      name: animal.animal_name,
    },
    counts: { ...counts, total },
    related,
    has_related_records: total > 0,
    uncertain,
  };
}

function countOffspring(animalId: number, animals?: AnimalSummary[]): number {
  if (!animals?.length) return 0;

  return animals.filter(
    (a) => a.mother_id === animalId || a.father_id === animalId,
  ).length;
}

async function buildFallbackSummary(
  animal: AnimalSummary,
  propertyAnimals?: AnimalSummary[],
): Promise<AnimalDeletionSummaryResult> {
  let media = 0;
  let events = 0;
  let movements = 0;
  let gotTimeline = false;

  try {
    const timeline = await apiFetch<AnimalTimelineResponse>(`/animals/${animal.id}/timeline`);
    media = timeline.meta.total_media;
    events = timeline.meta.total_events;
    movements = timeline.meta.total_movements;
    gotTimeline = true;
  } catch {
    try {
      const result = await apiFetch<{ media: unknown[] }>(`/animals/${animal.id}/media`);
      media = result.media.length;
    } catch {
      // Sem rotas novas na API — segue com contagem parcial.
    }
  }

  const offspring = countOffspring(animal.id, propertyAnimals);

  return buildSummary(
    animal,
    { media, events, movements, offspring },
    !gotTimeline,
  );
}

export async function fetchAnimalDeletionSummary(
  animal: AnimalSummary,
  propertyAnimals?: AnimalSummary[],
): Promise<AnimalDeletionSummaryResult> {
  try {
    const result = await apiFetch<{ summary: AnimalDeletionSummary }>(
      `/animals/${animal.id}/deletion-summary`,
    );
    return result.summary;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 0)) {
      return buildFallbackSummary(animal, propertyAnimals);
    }
    throw err;
  }
}
